import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Target, Clock, Users, DollarSign, BarChart3, ArrowRight, Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, FunnelChart, Funnel, LabelList, LineChart, Line, Area, AreaChart
} from 'recharts';

const COLORS = ['#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function SalesReportBuilder() {
  const [winLoss, setWinLoss] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [wl, vel, fn] = await Promise.all([
        reportsAPI.getWinLossReport({}),
        reportsAPI.getPipelineVelocity({}),
        reportsAPI.getConversionFunnel({}),
      ]);
      setWinLoss(wl.data);
      setVelocity(vel.data);
      setFunnel(fn.data);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="report-builder-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sales Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Win/loss analysis, pipeline velocity, and conversion funnels</p>
        </div>
      </div>

      <Tabs defaultValue="winloss">
        <TabsList data-testid="report-tabs">
          <TabsTrigger value="winloss" data-testid="tab-winloss">Win/Loss</TabsTrigger>
          <TabsTrigger value="velocity" data-testid="tab-velocity">Pipeline Velocity</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="tab-funnel">Conversion Funnel</TabsTrigger>
        </TabsList>

        <TabsContent value="winloss" className="space-y-4 mt-4">
          <WinLossTab data={winLoss} />
        </TabsContent>
        <TabsContent value="velocity" className="space-y-4 mt-4">
          <VelocityTab data={velocity} />
        </TabsContent>
        <TabsContent value="funnel" className="space-y-4 mt-4">
          <FunnelTab data={funnel} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, subtext, color = 'text-primary' }) {
  return (
    <Card data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10"><Icon className={`h-4 w-4 ${color}`} /></div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {subtext && <p className="text-[10px] text-muted-foreground/70">{subtext}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Win/Loss Tab ─── */
function WinLossTab({ data }) {
  if (!data) return null;
  const { summary, monthly_trend, loss_reasons, owner_performance } = data;

  const pieData = [
    { name: 'Won', value: summary.won, color: '#10b981' },
    { name: 'Lost', value: summary.lost, color: '#ef4444' },
    { name: 'Open', value: summary.open, color: '#3b82f6' },
  ].filter(d => d.value > 0);

  const lossData = Object.entries(loss_reasons || {}).map(([reason, count]) => ({ reason, count }));

  return (
    <div className="space-y-4" data-testid="winloss-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Target} label="Win Rate" value={`${summary.win_rate}%`} subtext={`${summary.won} won / ${summary.won + summary.lost} closed`} color="text-emerald-500" />
        <MetricCard icon={TrendingUp} label="Won Value" value={`$${(summary.won_value || 0).toLocaleString()}`} subtext={`${summary.won} deals`} color="text-emerald-500" />
        <MetricCard icon={TrendingDown} label="Lost Value" value={`$${(summary.lost_value || 0).toLocaleString()}`} subtext={`${summary.lost} deals`} color="text-red-500" />
        <MetricCard icon={DollarSign} label="Open Pipeline" value={`$${(summary.open_value || 0).toLocaleString()}`} subtext={`${summary.open} deals`} color="text-blue-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Deal Outcome Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Monthly Win/Loss Trend</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="won" fill="#10b981" name="Won" radius={[4, 4, 0, 0]} />
                <Bar dataKey="lost" fill="#ef4444" name="Lost" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {lossData.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-sm">Loss Reasons</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={lossData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="reason" type="category" width={120} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-sm">Rep Performance</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[200px] overflow-auto">
              {(owner_performance || []).map((o, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50" data-testid={`rep-row-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{o.owner}</p>
                    <p className="text-xs text-muted-foreground">{o.won}W / {o.lost}L / {o.total} total</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-500">{o.win_rate}%</p>
                    <p className="text-xs text-muted-foreground">${(o.won_value || 0).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {(!owner_performance || owner_performance.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No rep data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ─── Pipeline Velocity Tab ─── */
function VelocityTab({ data }) {
  if (!data) return null;
  const { summary, stage_velocity, stage_distribution } = data;

  return (
    <div className="space-y-4" data-testid="velocity-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Clock} label="Avg Cycle Time" value={`${summary.avg_cycle_days} days`} color="text-blue-500" />
        <MetricCard icon={BarChart3} label="Pipeline Deals" value={summary.pipeline_deals} color="text-amber-500" />
        <MetricCard icon={DollarSign} label="Pipeline Value" value={`$${(summary.total_pipeline_value || 0).toLocaleString()}`} color="text-emerald-500" />
        <MetricCard icon={Users} label="Total Analyzed" value={summary.total_deals_analyzed} color="text-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Average Days per Stage</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stage_velocity || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} label={{ value: 'Days', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                <Tooltip formatter={(val) => [`${val} days`]} />
                <Bar dataKey="avg_days" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Current Pipeline Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stage_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="count" fill="#3b82f6" name="Deals" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="value" fill="#10b981" name="Value ($)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Stage velocity detail table */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Stage Velocity Details</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 px-3">Stage</th>
                  <th className="text-right py-2 px-3">Avg Days</th>
                  <th className="text-right py-2 px-3">Min</th>
                  <th className="text-right py-2 px-3">Max</th>
                  <th className="text-right py-2 px-3">Deals</th>
                </tr>
              </thead>
              <tbody>
                {(stage_velocity || []).map((s, i) => (
                  <tr key={i} className="border-b border-muted/30" data-testid={`velocity-row-${i}`}>
                    <td className="py-2 px-3 font-medium">{s.stage}</td>
                    <td className="py-2 px-3 text-right font-bold">{s.avg_days}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{s.min_days}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">{s.max_days}</td>
                    <td className="py-2 px-3 text-right">{s.deals_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Conversion Funnel Tab ─── */
function FunnelTab({ data }) {
  if (!data) return null;
  const { summary, funnel_steps, deal_stage_funnel } = data;

  const maxCount = Math.max(...(funnel_steps || []).map(s => s.count), 1);

  return (
    <div className="space-y-4" data-testid="funnel-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Lead to Deal" value={`${summary.lead_to_deal_rate}%`} color="text-blue-500" />
        <MetricCard icon={Target} label="Deal to Win" value={`${summary.deal_to_win_rate}%`} color="text-emerald-500" />
        <MetricCard icon={DollarSign} label="Quote Close Rate" value={`${summary.quote_to_close_rate}%`} color="text-amber-500" />
        <MetricCard icon={TrendingUp} label="Invoice Paid Rate" value={`${summary.invoice_paid_rate}%`} color="text-purple-500" />
      </div>

      {/* Visual Funnel */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Sales Conversion Funnel</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="visual-funnel">
            {(funnel_steps || []).map((step, i) => {
              const widthPct = Math.max((step.count / maxCount) * 100, 8);
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                  <div className="w-20 text-xs font-medium text-right text-muted-foreground">{step.step}</div>
                  <div className="flex-1 relative">
                    <div
                      className="h-9 rounded-md flex items-center px-3 text-white text-xs font-bold transition-all"
                      style={{ width: `${widthPct}%`, backgroundColor: COLORS[i % COLORS.length] }}
                    >
                      {step.count}
                    </div>
                  </div>
                  <div className="w-16 text-xs text-muted-foreground text-right">{step.conversion}%</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Deal stage breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Deal Stage Breakdown</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={deal_stage_funnel || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="stage" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(val, name) => [name === 'value' ? `$${val.toLocaleString()}` : val, name === 'value' ? 'Value' : 'Count']} />
              <Legend />
              <Bar dataKey="count" fill="#3b82f6" name="Deals" radius={[4, 4, 0, 0]} />
              <Bar dataKey="value" fill="#f59e0b" name="Value ($)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
